import { Button, ConfirmDialog, FlashMessage, Modal, ProfileImageCrop } from "../../common";
import React, { useEffect, useRef, useState } from "react";
import UploadPictureSmall from "../icons/upload-profile-small.svg";
import EditIcon from "../icons/edit-icon.svg";
import DeleteIcon from "../icons/delete-icon.svg";
import PlayVideoIcon from "../icons/play-video-icon.svg";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import Image from "../../common/Image";
import VideoPlayerOverlay from "../../common/VideoPlayerOverlay";
import { rootReducersState } from "../../../reducers";
import { fetchSessionDataRequest } from "../../../reducers/auth/session.reducer";
import { deleteVideoRequest } from "../../../reducers/candidate/candidate.reducer";
import httpRequest from "../../../utils/httpRequest";
import API_URLS from "../../../utils/apiUrls";
import VideoProgress from "../../../pages/videoStudio/components/VideoProgess";
import { validateFileSize } from "../../helpers/formValidation";
import AvatarImage from "../../common/AvtarImage";
import navigate from "../../../utils/history";
import {
  createVideoRequest,
  createVideoProgress,
  createVideoSuccess,
  createVideoError,
  createVideoReset,
  videoStudioListRequest,
} from "../../../reducers/candidate/videoStudio.reducer";
import { useOptionalSocket } from "../../common/SocketIo";
import { getMetadata as getVideoMetadata } from "video-metadata-thumbnails";
import axios from "axios";
import { uuid } from "../../../utils/helper";
import {
  VideoMimeTypes,
  bytesMb,
} from "../../../utils/appConstants";
import {
  getVideoDurationLimitMessage,
  isWithinVideoDurationLimit,
} from "../../../pages/videoStudio/videoStudioTimeline.utils";

interface ProfileImageDataProps {
  profile_image: string;
  profile_video: any;
}
const ProfileImage = ({
  profileImageData,
  isPublicPage,
  openUploadRequest,
  onOpenUploadHandled,
}: {
  // `profileImageData` can be null/undefined initially while async data loads.
  profileImageData?: ProfileImageDataProps | null;
  isPublicPage?: boolean;
  openUploadRequest?: boolean;
  onOpenUploadHandled?: () => void;
}) => {
  const SAVE_PROGRESS_EVENT = "videoStudioSaveProgress";
  const socket = useOptionalSocket();
  const [videoOrImageModal, setVideoOrImageModal] = useState(false);
  const [videoFile, setVideoFile] = useState<any>(null);
  const [showCropImage, setShowCropImage] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [cropPreviewImage, setPreviewImage] = useState<string>("");
  const [uploadImage, setUploadImage] = useState<any>();
  const [uploadLoading, setUploadLoading] = useState(false);
  const [profileImage, setProfileImage] = useState<string>(() =>
    profileImageData?.profile_image ? profileImageData.profile_image : ''
  );
  const [videoModal, setVideoModal] = useState(false);
  const [sureAboutVideoDelete, setSureAboutVideoDelete] = useState(false);
  const dispatch = useDispatch();
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const deleteInitiatedRef = useRef(false);
  const session = useSelector(({ session }: rootReducersState) => session);
  const videoStudioCreate = useSelector(
    ({ candidate }: rootReducersState) => candidate.videoStudio.create
  );
  const videoStudioLoading = videoStudioCreate.loading;
  const videoStudioProgress = videoStudioCreate.progress ?? 0;
  const videoStudioJobId = videoStudioCreate.jobId;
  const videoStudioFlag = videoStudioCreate.flag;
  const { loading: videoDeleteLoading, deleteflag: videoDeleteFlag, message: videoDeleteMessage } =
    useSelector(({ candidate }: rootReducersState) => candidate.candidates);

  const profileVideo = profileImageData?.profile_video?.[0]
    ? profileImageData?.profile_video?.[0]
    : {};

  useEffect(() => {
    // Keep internal `profileImage` state in sync with incoming props.
    setProfileImage(
      profileImageData?.profile_image ? profileImageData.profile_image : ''
    );
  }, [profileImageData?.profile_image]);

  useEffect(() => {
    if (!openUploadRequest || isPublicPage) return;
    setVideoOrImageModal(true);
    onOpenUploadHandled?.();
  }, [openUploadRequest, isPublicPage, onOpenUploadHandled]);

  const uploadProfileVideoViaStudio = async (file: File) => {
    const videoMetaData = await getVideoMetadata(file);
    const duration = videoMetaData.duration;

    if (!isWithinVideoDurationLimit(duration)) {
      FlashMessage(getVideoDurationLimitMessage(), "error");
      return false;
    }

    const clipId = uuid();
    const formData = new FormData();
    formData.append(clipId, file, file.name);
    formData.append(
      "videoMeta",
      JSON.stringify([
        {
          video: clipId,
          startTime: 0,
          endTime: duration,
          mirror: false,
        },
      ])
    );
    formData.append("title", "Profile Video");
    formData.append("description", "");
    formData.append("exportTo", "profile");

    const cancelToken = axios.CancelToken.source();
    dispatch(createVideoRequest({ formData, cancelToken }));
    return true;
  };

  useEffect(() => {
    if (!socket || !videoStudioJobId) {
      return;
    }

    const onSaveProgress = (data: {
      jobId?: string;
      status?: string;
      progress?: number;
      message?: string;
    }) => {
      if (data.jobId !== videoStudioJobId) {
        return;
      }

      const status = data.status || "processing";

      if (status === "success") {
        dispatch(
          createVideoSuccess({
            message: data.message || "Video saved successfully",
            data: {},
          })
        );
      } else if (status === "failed") {
        dispatch(
          createVideoError({
            message: data.message || "Unable to save video",
          })
        );
      } else {
        dispatch(createVideoProgress({ progress: data.progress ?? 0 }));
      }
    };

    socket.on(SAVE_PROGRESS_EVENT, onSaveProgress);

    return () => {
      socket.off(SAVE_PROGRESS_EVENT, onSaveProgress);
    };
  }, [dispatch, socket, videoStudioJobId]);

  useEffect(() => {
    if (videoStudioFlag === true) {
      FlashMessage("Files uploaded successfully", "success");
      dispatch(videoStudioListRequest({}));
      dispatch(fetchSessionDataRequest());
      setVideoFile(null);
      setUploadImage(null);
      if (videoInputRef.current) videoInputRef.current.value = "";
      if (fileInputRef.current) fileInputRef.current.value = "";
      setVideoOrImageModal(false);
      setUploadLoading(false);
      dispatch(createVideoReset());
    } else if (videoStudioFlag === false) {
      FlashMessage(videoStudioCreate.message || "Unable to save video", "error");
      setUploadLoading(false);
      dispatch(createVideoReset());
    }
  }, [dispatch, videoStudioCreate.message, videoStudioFlag]);

  useEffect(() => {
    if (!deleteInitiatedRef.current) {
      return;
    }

    if (videoDeleteFlag === true) {
      FlashMessage("Video deleted successfully", "success");
      deleteInitiatedRef.current = false;
      setSureAboutVideoDelete(false);
      setVideoModal(false);
      dispatch(videoStudioListRequest({}));
      dispatch(fetchSessionDataRequest());
      return;
    }

    if (!videoDeleteLoading && videoDeleteFlag === false) {
      FlashMessage(videoDeleteMessage || "Unable to delete video", "error");
      deleteInitiatedRef.current = false;
      setSureAboutVideoDelete(false);
    }
  }, [
    dispatch,
    videoDeleteFlag,
    videoDeleteLoading,
    videoDeleteMessage,
  ]);

  // Handle video file selection
  const handleVideoChange = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    if (!VideoMimeTypes.includes(file.type)) {
      FlashMessage("Please select a valid mp4 or mov file", "error");
      return;
    }

    if (file.size > bytesMb.mb_50) {
      FlashMessage("File size should be 50MB or less", "error");
      return;
    }

    setVideoFile(file);
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileSize = validateFileSize(file.size, 5);
      if (fileSize) {
        FlashMessage(fileSize, "error");
      }
      const imageUrl = URL.createObjectURL(file);
      setCropImage(imageUrl);
      setShowCropImage(true);
    }
  };

  const handleCropImage = async (blobData: Blob) => {
    setUploadImage(blobData);
    const src = URL.createObjectURL(blobData);
    setPreviewImage(src);
  };

  const handleProfileSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const uploadedImage = Boolean(uploadImage);
    const uploadedVideo = Boolean(videoFile);

    try {
      if (uploadedImage || uploadedVideo) {
        setUploadLoading(true);
      }

      // Upload Image if available
      if (uploadedImage) {
        const imageFormData = new FormData();
        imageFormData.append("files", uploadImage, "profile.jpg");
        await httpRequest().post(
          `${API_URLS.candidates}profile/image`,
          imageFormData
        );

        if (cropPreviewImage) {
          setProfileImage(cropPreviewImage);
        }
      }

      // Upload Video via video studio (exportTo: profile)
      if (uploadedVideo) {
        const started = await uploadProfileVideoViaStudio(videoFile);
        if (!started) {
          setUploadLoading(false);
          return;
        }

        if (!uploadedImage) {
          return;
        }
      }

      if (uploadedImage && !uploadedVideo) {
        FlashMessage("Files uploaded successfully", "success");
        setUploadImage(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setVideoOrImageModal(false);
        setUploadLoading(false);
        dispatch(fetchSessionDataRequest());
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      FlashMessage("Error uploading files", "error");
      setUploadLoading(false);
      setVideoOrImageModal(false);
    }
  };

  const handleOpenDeleteConfirm = () => {
    setVideoModal(false);
    setSureAboutVideoDelete(true);
  };

  const _handleDeleteVideo = () => {
    if (!_.isEmpty(_.get(profileVideo, "video_file", ""))) {
      deleteInitiatedRef.current = true;
      dispatch(deleteVideoRequest());
    } else {
      FlashMessage("No video to delete.", "error");
      setSureAboutVideoDelete(false);
    }
  };
  return (
    <>
      <div className="profile-picture-wrapper">
        <div
          className="upload-main-picture"
          onClick={() => {
            if (!isPublicPage) setVideoOrImageModal(true);
          }}
        >
          <AvatarImage
            src={profileImage}
            size="md"
            className={profileImage ? "user-profile-image" : ""}
          ></AvatarImage>
        </div>
        {
          !isPublicPage && (
            <div className="upload-icon">
              {!_.isEmpty(_.get(profileVideo, "video_file", "")) ? (
                <span
                  className={`play-button `}
                  onClick={() => setVideoModal(true)}
                >
                  <Image src={PlayVideoIcon} alt="Play icon" />
                </span>
              ) : (
                <img
                  src={UploadPictureSmall}
                  alt="upload"
                  onClick={() => {
                    if (!isPublicPage) setVideoOrImageModal(true);
                  }}
                />
              )}
            </div>
          )
        }

      </div>

      <Modal
        className="candidate-profile-modal upload-profile-modal"
        isOpen={videoOrImageModal}
        onClose={() => setVideoOrImageModal(false)}
        title="What would you like to upload?"
        visible={videoOrImageModal}
        size="medium"
      >
        {uploadLoading || videoStudioLoading ? (
          <VideoProgress
            progress={videoStudioProgress}
            handleOnClose={() => {
              setUploadLoading(false);
              dispatch(createVideoReset());
            }}
          />
        ) : (
          <div className="modal-body-wrapper">
            <form
              onSubmit={handleProfileSubmit}
              className="profile-upload-wrapper"
            >
              {uploadImage ? (
                <div className="uploaded-profile-wrapper">
                  <div className="profile-header">
                    <b>Profile Picture</b>
                    <div
                      className="edit-btn"
                      onClick={() => setShowCropImage(true)}
                    >
                      <img src={EditIcon} alt="icon" />
                      <span>Edit</span>
                    </div>
                  </div>
                  <div className="uploaded-img">
                    <img src={cropPreviewImage} alt="profile" />
                  </div>
                </div>
              ) : (
                <div
                  className="profile-upload-card"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="profile-icon">
                    <img src={UploadPictureSmall} alt="icon" />
                  </div>
                  <b>Upload profile picture</b>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                </div>
              )}
              {videoFile ? (
                <div className="video-uploaded-wrapper">
                  <h6>Profile Video</h6>
                  <div className="uploaded-video-card">
                    <div className="profile-icon">
                      <img src={PlayVideoIcon} alt="icon" />
                    </div>
                    <div className="video-content">
                      <div className="video-info">
                        <b>{videoFile?.name}</b>
                        <span>
                          {`Selected on ${moment().format("MMMM DD, YYYY")}`}
                        </span>
                      </div>
                      <div
                        className="video-action"
                        onClick={() => setVideoFile(null)}
                      >
                        <img src={DeleteIcon} alt="icon" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="profile-upload-card"
                  onClick={() => videoInputRef.current?.click()}
                >
                  <div className="profile-icon">
                    <img src={UploadPictureSmall} alt="icon" />
                  </div>
                  <b>Upload profile video</b>
                  <input
                    type="file"
                    accept={VideoMimeTypes.join(",")}
                    ref={videoInputRef}
                    onChange={handleVideoChange}
                    style={{ display: "none" }}
                  />
                </div>
              )}

              {(videoFile || uploadImage) && (
                <div className="btn-wrapper">
                  <Button
                    variant="outlined"
                    className="outlined"
                    onClick={() => setVideoOrImageModal(false)}
                    color="none"
                  >
                    Cancel
                  </Button>
                  <Button className="save-btn" variant="contained" type="submit">
                    Submit
                  </Button>
                </div>
              )}
            </form>
          </div>
        )}
      </Modal>

      <VideoPlayerOverlay
        open={videoModal}
        onClose={() => setVideoModal(false)}
        videoUrl={profileVideo?.video_file}
        videoThumbnail={profileVideo?.video_thumbnail}
        title={profileVideo?.title || "Profile Video"}
        footer={
          !isPublicPage ? (
            <>
              <Button
                color="secondary"
                className="submit-button text-uppercase"
                onClick={handleOpenDeleteConfirm}
              >
                Delete
              </Button>
              <Button
                color="primary"
                className="submit-button text-uppercase"
                onClick={() => {
                  const videoId =
                    profileVideo?.id ||
                    session?.currentUser?.primary_video_id;
                  if (videoId) {
                    navigate(`/video-studio/edit/${videoId}`);
                  }
                }}
              >
                Edit
              </Button>
            </>
          ) : undefined
        }
      />
      <ConfirmDialog
        visible={sureAboutVideoDelete}
        title="Delete Video"
        bodyText="Are you sure you want to delete this video?"
        confirmText="Delete"
        cancelText="Cancel"
        loading={videoDeleteLoading}
        onConfirm={_handleDeleteVideo}
        onCancel={() => setSureAboutVideoDelete(false)}
      />
      <ProfileImageCrop
        layout="profileImage"
        visible={showCropImage}
        cropImage={cropImage}
        onClose={() => setShowCropImage(false)}
        onCrop={handleCropImage}
        title="Customize Image"
      />
    </>
  );
};

export default ProfileImage;
